import type {
  LibraryDependency,
  LibraryItemView,
} from '@/lib/api/generated/model';
import { isInWorkspace } from './presence';

/** What installing would do to one dependency (spec 105 data-model). */
export type DependencyPlan = 'add' | 'stays' | 'missing';

export interface PlannedDependency {
  dep: LibraryDependency;
  /** The library's copy, absent when the library does not hold it. */
  found: LibraryItemView | undefined;
  plan: DependencyPlan;
}

export interface InstallSummary {
  /** Every dependency in the manifest's order, with what would happen to it. */
  rows: PlannedDependency[];
  toAdd: LibraryDependency[];
  leftAlone: LibraryDependency[];
}

/**
 * What Install will do with an item's dependencies, from their states.
 *
 * Only a dependency the workspace does not hold is added; everything else is
 * left alone, whatever its state, because an install never replaces (spec 104
 * FR-015). The item itself is not listed: the drawer's action speaks for it.
 *
 * The two lists are derived from `rows` rather than gathered beside them, so
 * the sentence under the list and the rows in it cannot disagree about how many
 * items an install adds (spec 105 FR-013).
 */
export function summarizeInstall(
  item: LibraryItemView,
  items: readonly LibraryItemView[],
): InstallSummary {
  const byId = new Map(items.map((i) => [`${i.kind}/${i.key}`, i]));
  const rows: PlannedDependency[] = (item.requires ?? []).map((dep) => {
    const found = byId.get(`${dep.kind}/${dep.key}`);
    let plan: DependencyPlan;
    if (found === undefined) plan = 'missing';
    else if (isInWorkspace(found.state)) plan = 'stays';
    else plan = 'add';
    return { dep, found, plan };
  });
  return {
    rows,
    // A dependency the library does not hold is still something the install
    // would have to bring, and spec 104's service refuses the workflow for it;
    // counting it as "left alone" would read as "already fine".
    toAdd: rows.filter((r) => r.plan !== 'stays').map((r) => r.dep),
    leftAlone: rows.filter((r) => r.plan === 'stays').map((r) => r.dep),
  };
}
